/**
 * Sliding-window crash detector. Pure function — extracted from
 * `worker-client.ts` so the poison-window logic can be unit-tested
 * without spinning up a real Worker.
 *
 * If 3 or more crashes land inside any 10-second window, the worker is
 * marked poisoned and the caller stops respawning. The window slides
 * with each call: events older than `WINDOW_MS` are evicted.
 */

export type CrashTracker = {
  /** Timestamps of recent crashes, oldest first. Mutated in place. */
  events: number[];
};

export type CrashResult = {
  /** True iff the threshold was hit on this event. */
  poisoned: boolean;
  /** Crashes counted inside the current window after this event. */
  countInWindow: number;
};

const WINDOW_MS = 10_000;
const THRESHOLD = 3;

/**
 * Record a crash at `now` and report whether the tracker has crossed
 * the poison threshold. Mutates `tracker.events`.
 */
export function recordCrash(
  tracker: CrashTracker,
  now: number,
): CrashResult {
  // Evict events outside the sliding window.
  while (tracker.events.length > 0 && now - tracker.events[0]! > WINDOW_MS) {
    tracker.events.shift();
  }
  tracker.events.push(now);
  const countInWindow = tracker.events.length;
  return {
    poisoned: countInWindow >= THRESHOLD,
    countInWindow,
  };
}
